import { Button } from '@mui/material';
import { ColDef, EditableCallbackParams, GetRowIdParams } from 'ag-grid-community';
import { useEffect, useState } from 'react';
import { MarkItViewApi } from '../../../../Apis/Apis';
import {
  CommodityManagementSiteAccessModel,
  CommodityManagementSiteModel,
  CommodityManagementUserPermissions,
  CompanyModel,
  MarketViewUserModel,
} from '../../../../Generated/Mark-It-View-Api';
import { EnumHelper } from '../../../../Helpers';
import { StoneXGrid, StoneXRow } from '../../../StoneX';
import { StoneXAutocomplete, StoneXModal, StoneXMultiAutocomplete, StoneXTextField } from '../../../StoneXMui';

export interface AddUserRequest {
  marketViewUserGuid?: string;
  firstName: string;
  lastName: string;
  email: string;
  companyGuid?: string;
  commodityManagementUserSiteAccesses?: CommodityManagementSiteAccessModel[];
}

interface UpdateUserModalProps {
  open: boolean;
  onClose: () => void;
  onUserSave: (user: AddUserRequest) => void;
  user?: MarketViewUserModel;
  companies: CompanyModel[];
}

export function UpdateUserModal(props: UpdateUserModalProps) {
  const { open, onClose, onUserSave, user, companies } = props;
  const [firstName, setFirstName] = useState<string>();
  const [lastName, setLastName] = useState<string>();
  const [email, setEmail] = useState<string>();
  const [selectedCompany, setSelectedCompany] = useState<CompanyModel | null>(null);
  const [userSites, setUserSites] = useState<CommodityManagementSiteModel[]>([]);

  const [sites, setSites] = useState<CommodityManagementSiteModel[]>([]);

  const actionLabel = props.user ? 'Update' : 'Add';

  useEffect(() => {
    setFirstName(user?.firstName ?? '');
    setLastName(user?.lastName ?? '');
    setEmail(user?.email ?? '');
    setSelectedCompany(companies.find((company) => company.companyGuid === user?.companyGuid) ?? null);
    setUserSites(getUserSites());
  }, [user, sites, companies]);

  useEffect(() => {
    MarkItViewApi.listCommodityManagementSites(null).then((response) => setSites(response.data.rows ?? []));
  }, []);

  function getUserSites() {
    if (user && user.commodityManagementUserSiteAccesses && sites.length > 0) {
      const userSiteGuids = user.commodityManagementUserSiteAccesses.map((siteAccess) => siteAccess.siteGuid);
      const matchedSites = sites.filter((site) => userSiteGuids.includes(site.siteGuid));
      return matchedSites;
    }

    return [];
  }

  function updateUser() {
    onUserSave({
      marketViewUserGuid: user?.marketViewUserGuid,
      firstName: firstName!,
      lastName: lastName!,
      email: email!,
      companyGuid: selectedCompany?.companyGuid,
      commodityManagementUserSiteAccesses: siteAccesses,
    });

    onClose();
  }

  const permissionKeys = Object.values(CommodityManagementUserPermissions);

  const [siteAccesses, setSiteAccesses] = useState<CommodityManagementSiteAccessModel[]>(getSiteAccess());
  useEffect(() => setSiteAccesses(getSiteAccess()), [userSites, user]);

  function getSiteAccess() {
    return userSites.map((site) => {
      const existing = user?.commodityManagementUserSiteAccesses?.find((siteAccess) => siteAccess.siteGuid === site.siteGuid);
      return (
        existing || {
          siteGuid: site.siteGuid,
          siteName: site.siteName,
          isSiteManager: false,
          permissions: [],
        }
      );
    });
  }

  function updateSiteAccess(siteGuid: string, updated: Partial<CommodityManagementSiteAccessModel>) {
    setSiteAccesses((previous) => previous.map((access) => (access.siteGuid === siteGuid ? { ...access, ...updated } : access)));
  }

  const columns: ColDef[] = [
    {
      headerName: 'Site Name',
      field: 'siteName',
      checkboxSelection: false,
      editable: false,
      minWidth: 80,
      flex: 1,
    },
    {
      headerName: 'Site Manager',
      field: 'isSiteManager',
      width: 130,
      cellRenderer: 'agCheckboxCellRenderer',
      cellEditor: 'agCheckboxCellEditor',
      valueGetter: (params: any) => params.data.isSiteManager,
      valueSetter: (params: any) => {
        const checked = params.newValue;
        params.data.isSiteManager = checked;
        updateSiteAccess(params.data.siteGuid, {
          isSiteManager: checked,
          permissions: checked ? Object.values(CommodityManagementUserPermissions) : [],
        });
        return true;
      },
      editable: (params: EditableCallbackParams) => !user?.isAdministrator,
    },
    ...permissionKeys.map((permission: string) => ({
      headerName: EnumHelper.enumToString(permission).replace('Manage', ''),
      field: permission,
      minWidth: 80,
      flex: 1,
      cellRenderer: 'agCheckboxCellRenderer',
      cellEditor: 'agCheckboxCellEditor',
      editable: (params: EditableCallbackParams) => !params.data.isSiteManager,
      valueGetter: (params: any) => params.data.isSiteManager || params.data.permissions?.includes(permission),
      valueSetter: (params: any) => {
        const checked = params.newValue;
        const permissions = params.data.permissions || [];
        if (checked && !permissions.includes(permission)) {
          params.data.permissions = [...permissions, permission];
        } else if (!checked && permissions.includes(permission)) {
          params.data.permissions = permissions.filter((value: string) => value !== permission);
        }
        return true;
      },
    })),
  ];

  return (
    <StoneXModal header={`${actionLabel} User`} open={open} onClose={onClose} width="100%">
      <StoneXRow align="start">
        <StoneXTextField label="First Name" value={firstName} onUpdate={setFirstName} />
        <StoneXTextField label="Last Name" value={lastName} onUpdate={setLastName} />
        <StoneXTextField label="Email" value={email} onUpdate={setEmail} />
        <StoneXAutocomplete
          width="200px"
          label="Company"
          options={companies}
          getOptionLabel={(company) => company.name}
          value={selectedCompany}
          onUpdate={setSelectedCompany}
          isOptionEqualToValue={(option, value) => option.companyGuid === value.companyGuid}
        />
      </StoneXRow>

      <StoneXRow align="start">
        <StoneXMultiAutocomplete
          width="520px"
          label="Commodity Management Sites"
          options={sites}
          getOptionLabel={(site) => site.siteName}
          value={userSites}
          onUpdate={setUserSites}
        />
      </StoneXRow>

      <StoneXRow marginDirection="topAndBottom">
        <div style={{ width: '100%' }}>
          <StoneXGrid
            rows={siteAccesses}
            columns={columns}
            getRowId={(params: GetRowIdParams) => params.data.siteGuid}
            hideGridOptions
            gridOptions={{
              suppressMultiRangeSelection: true,
              stopEditingWhenCellsLoseFocus: true,
            }}
          />
        </div>
      </StoneXRow>

      <StoneXRow justify="end">
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={updateUser}>{`${actionLabel} User`}</Button>
      </StoneXRow>
    </StoneXModal>
  );
}
